import { useCallback, useRef, useState } from 'react';

import { CheckCircleIcon } from '@braskai/icons';
import { Button, Divider, Modal, ModalActions, ModalContent, ModalWrapper, Typography } from '@braskai/ui-kit';

import { Box, Stack } from '@mui/material';

import { DemoBookingCalendarModal, useBookSalesDemoStore } from '@/entities/demoBooking';
import { useCurrentUser } from '@/entities/user/currentUser';

import { BOOK_SALES_DEMO_MEETING_LINK } from '@/shared/constants/docLinks';
import { closeAllModalsExcept } from '@/shared/lib/modal';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { formatBookedSlot } from '../../lib/formatBookedSlot';

const BookSalesDemoModalContent = () => {
  const close = useBookSalesDemoStore((state) => state.close);
  const source = useBookSalesDemoStore((state) => state.source);
  const sourceText = useBookSalesDemoStore((state) => state.sourceText);
  const { data: user } = useCurrentUser();

  const [booked, setBooked] = useState<{ slot?: string }>();

  const bookedRef = useRef(false);

  const handleBooked = useCallback(
    (startsAt?: Date) => {
      if (bookedRef.current) {
        return;
      }

      bookedRef.current = true;
      const slot = formatBookedSlot(startsAt);
      setBooked({ slot });
      MixpanelEvents.bookSalesDemoBooked({
        source: source ?? undefined,
        sourceText: sourceText ?? undefined,
        ...(slot && { slot }),
      });

      closeAllModalsExcept(useBookSalesDemoStore);
    },
    [source, sourceText],
  );

  const handleClose = useCallback(() => {
    if (!bookedRef.current) {
      MixpanelEvents.bookSalesDemoDismissed({ source: source ?? undefined, sourceText: sourceText ?? undefined });
    }

    close();
  }, [close, source, sourceText]);

  const handleLoadError = useCallback(() => {
    MixpanelEvents.bookSalesDemoEmbedFailed({ source: source ?? undefined, sourceText: sourceText ?? undefined });
  }, [source, sourceText]);

  if (booked) {
    const heading = booked.slot ? `You're booked for ${booked.slot}` : "You're booked!";

    return (
      <Modal
        size="sm"
        // Same lift as the calendar step: the confirmation can outlive a raw MUI modal.
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        open
        onClose={handleClose}
      >
        <ModalContent>
          <Stack
            sx={{
              '@keyframes bookSalesDemoPop': {
                from: { opacity: 0, transform: 'scale(0.85)' },
                to: { opacity: 1, transform: 'scale(1)' },
              },
              '@keyframes bookSalesDemoRise': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
            gap={20}
          >
            <Stack alignItems="center" direction="row" gap={10}>
              <Box
                sx={{
                  animation: 'bookSalesDemoPop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both',
                  color: 'success.600',
                  display: 'flex',
                  flexShrink: 0,
                }}
              >
                <CheckCircleIcon height={28} width={28} />
              </Box>

              <Typography
                color="grey.900"
                data-testid="book-sales-demo-booked-slot"
                sx={{ animation: 'bookSalesDemoRise 0.4s ease-out 0.06s both' }}
                variant="title-4-sb"
              >
                {heading}
              </Typography>
            </Stack>

            <Typography
              color="grey.500"
              sx={{ animation: 'bookSalesDemoRise 0.4s ease-out 0.1s both' }}
              variant="body-1"
            >
              An invite with the meeting link has been sent to your email.
            </Typography>

            <Divider sx={{ animation: 'bookSalesDemoRise 0.4s ease-out 0.14s both' }} />

            <Typography
              color="grey.700"
              sx={{ animation: 'bookSalesDemoRise 0.4s ease-out 0.18s both' }}
              variant="body-1"
            >
              <Typography color="grey.700" component="span" variant="body-1-md">
                What we&apos;ll cover:
              </Typography>{' '}
              your localization use case and the best way to run it in Rask, how it works across your team, and clear
              pricing structure.
            </Typography>
          </Stack>
        </ModalContent>

        <ModalActions>
          <Button data-testid="book-sales-demo-modal-done" size="lg" variant="primary" fullWidth onClick={handleClose}>
            See you soon
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  return (
    <DemoBookingCalendarModal
      email={user?.email}
      meetingUrl={BOOK_SALES_DEMO_MEETING_LINK}
      onBooked={handleBooked}
      onClose={handleClose}
      onLoadError={handleLoadError}
    />
  );
};

export const BookSalesDemoModal = () => (
  <ModalWrapper hook={useBookSalesDemoStore}>
    <BookSalesDemoModalContent />
  </ModalWrapper>
);
